import { useMemo, useState } from "react";
import HisseSatisEkleDrawer from "./components/HisseSatisEkleDrawer";
import MoneyInput from "../../../components/form/MoneyInput";
import { useAnimals } from "../../../hooks/useAnimals";
import { useBankTransfers } from "../../../hooks/useBankTransfers";
import { useCustomers } from "../../../hooks/useCustomers";
import { useUsers } from "../../../hooks/useUsers";
import { usePayments } from "../../../hooks/usePayments";
import { useSharePresales } from "../../../hooks/useSharePresales";
import { isBankTransferActive } from "../../../lib/bankTransferStore";
import { createClientNumericId } from "../../../lib/id";
import { formatTry, parseMoneyInput } from "../../../lib/money";
import { downloadSharePresaleWorkbook } from "../../../lib/reportWorkbook";
import { deleteSharePresaleOperationRequest, linkSharePresalesToAnimalBatchOperationRequest, unlinkSharePresaleFromAnimalOperationRequest } from "../../../lib/api";
import { applyOperationResult } from "../../../lib/operationRefresh";
import UserModal, { type UserFormData } from "../kullanici-yonetimi/components/UserModal";
import { getNextUserId } from "../../../store/usersStore";
import { sendSmsByTemplate } from "../../../lib/smsService";
import { getDisplayKupeNo, getDisplaySiparisNo } from "../../../lib/animalLabels";
import type { PaymentRecord } from "../../../mocks/payments";
import type {
  SharePresalePackage,
  SharePresalePaymentType,
  SharePresaleRecord,
  SharePresaleStatus,
} from "../../../lib/sharePresalesStore";

const STATUS_OPTIONS: Array<SharePresaleStatus | "Hepsi"> = [
  "Hepsi",
  "Bağlanmadı",
  "Kısmi Bağlandı",
  "Bağlandı",
  "İptal",
];

const PRESALE_PAYMENT_PREFIX = "Hisse ön satış kapora:";

type PackageFilter = number | "Hepsi";

function today() {
  return new Date().toISOString().slice(0, 10);
}

function normalizeText(value?: string) {
  return (value ?? "").toLocaleLowerCase("tr-TR").replace(/\s+/g, " ").trim();
}

function makeSaleNo(existingCount: number) {
  return `HS-${today().replace(/-/g, "")}-${String(existingCount + 1).padStart(3, "0")}`;
}

function statusBadgeClass(status: SharePresaleStatus) {
  if (status === "Bağlandı") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (status === "Kısmi Bağlandı") return "bg-amber-50 text-amber-700 border-amber-200";
  if (status === "İptal") return "bg-red-50 text-red-700 border-red-200";
  return "bg-sky-50 text-sky-700 border-sky-200";
}

function formatTransferDate(value?: string) {
  if (!value) return "—";
  const [year, month, day] = value.slice(0, 10).split("-");
  return year && month && day ? `${day}.${month}.${year}` : value;
}

function dateInRange(value: string | undefined, from: string, to: string) {
  const date = String(value ?? "").slice(0, 10);
  if (!date) return !from && !to;
  if (from && date < from) return false;
  if (to && date > to) return false;
  return true;
}

function linkedShareCount(sale: SharePresaleRecord) {
  if (sale.durum === "Bağlandı") return sale.adet;
  const explicit = Array.isArray(sale.linkedShareIds) ? sale.linkedShareIds.length : 0;
  if (explicit > 0) return Math.min(sale.adet, explicit);
  if (sale.durum === "Kısmi Bağlandı") {
    const linkedAnimals = Array.isArray(sale.linkedAnimalIds) ? sale.linkedAnimalIds.length : 0;
    return Math.min(sale.adet, linkedAnimals);
  }
  return 0;
}

function cleanupTransferNote(note: string | undefined, saleNo: string) {
  return String(note ?? "")
    .split(" | ")
    .map((item) => item.trim())
    .filter((item) => item && !item.includes(saleNo))
    .join(" | ");
}

function isPresalePaymentForSale(payment: PaymentRecord, saleNo: string) {
  return (
    payment.animalId === 0 &&
    (payment.hisseNo === saleNo || String(payment.aciklama ?? "").includes(`${PRESALE_PAYMENT_PREFIX} ${saleNo}`))
  );
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-farm border border-farm-border bg-white p-3 shadow-sm">
      <p className="font-body text-[10px] font-semibold uppercase tracking-wide text-farm-muted">{label}</p>
      <p className="mt-1 font-heading text-lg font-bold text-farm-brown">{value}</p>
      {sub && <p className="mt-1 font-body text-[10px] text-farm-muted">{sub}</p>}
    </div>
  );
}

export default function HisseSatislariPage() {
  const { animals } = useAnimals();
  const { customers, setAllCustomers } = useCustomers();
  const { setAllUsers } = useUsers();
  const { payments, setAllPayments } = usePayments();
  const { transfers, setAllBankTransfers } = useBankTransfers();
  const { sharePresales, setSharePresales } = useSharePresales();

  const [editingPackageId, setEditingPackageId] = useState<number | null>(null);
  const [packageTitle, setPackageTitle] = useState("");
  const [packageAnimalType, setPackageAnimalType] = useState("Dana");
  const [packageAmount, setPackageAmount] = useState("");
  const [packageNote, setPackageNote] = useState("");

  const [saleDate, setSaleDate] = useState(today());
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [depositAmount, setDepositAmount] = useState("");
  const [paymentType, setPaymentType] = useState<SharePresalePaymentType>("Nakit");
  const [havaleNo, setHavaleNo] = useState("");
  const [saleNote, setSaleNote] = useState("");

  const [statusFilter, setStatusFilter] = useState<SharePresaleStatus | "Hepsi">("Hepsi");
  const [packageFilter, setPackageFilter] = useState<PackageFilter>("Hepsi");
  const [search, setSearch] = useState("");
  const [dateNoFilter, setDateNoFilter] = useState("");
  const [customerColumnFilter, setCustomerColumnFilter] = useState("");
  const [packageColumnFilter, setPackageColumnFilter] = useState("");
  const [saleNoteFilter, setSaleNoteFilter] = useState("");
  const [kupeSiparisFilter, setKupeSiparisFilter] = useState("");
  const [groupColumnFilter, setGroupColumnFilter] = useState<"Hepsi" | "Grupsuz" | number>("Hepsi");
  const [transportColumnFilter, setTransportColumnFilter] = useState<"Hepsi" | "Var" | "Yok">("Hepsi");
  const [adetFilter, setAdetFilter] = useState("");
  const [totalFilter, setTotalFilter] = useState("");
  const [depositFilter, setDepositFilter] = useState("");
  const [remainingFilter, setRemainingFilter] = useState("");
  const [paymentColumnFilter, setPaymentColumnFilter] = useState<"Hepsi" | "Nakit" | "Havale">("Hepsi");
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [definitionsPanelOpen, setDefinitionsPanelOpen] = useState(true);
  const [saleDrawerOpen, setSaleDrawerOpen] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [editingSale, setEditingSale] = useState<SharePresaleRecord | null>(null);
  const [selectedSaleIds, setSelectedSaleIds] = useState<number[]>([]);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [selectedLinkAnimalId, setSelectedLinkAnimalId] = useState<number | null>(null);
  const [linkKupeSearch, setLinkKupeSearch] = useState("");
  const [linkSiparisSearch, setLinkSiparisSearch] = useState("");
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [selectedGroupNo, setSelectedGroupNo] = useState<number | null>(null);
  const [pendingGroupNo, setPendingGroupNo] = useState<number | null>(null);
  const [manualGroupNumbers, setManualGroupNumbers] = useState<number[]>(() => {
    try {
      const raw = window.localStorage.getItem("evrenciftligi_share_presale_groups_v1");
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed)) {
        return parsed.map(Number).filter((item) => Number.isFinite(item) && item > 0);
      }
    } catch {
      // localStorage okunamazsa varsayılana düş
    }
    return [1, 2, 3, 4, 5, 6];
  });

  const [transferPickerOpen, setTransferPickerOpen] = useState(false);
  const [selectedTransferIds, setSelectedTransferIds] = useState<string[]>([]);
  const [transferDateFrom, setTransferDateFrom] = useState("");
  const [transferDateTo, setTransferDateTo] = useState("");
  const [transferDescriptionFilter, setTransferDescriptionFilter] = useState("");

  const activePackages = useMemo(
    () => [...sharePresales.packages].filter((item) => item.aktif).sort((a, b) => a.sıraNo - b.sıraNo || a.id - b.id),
    [sharePresales.packages]
  );

  const allPackagesSorted = useMemo(
    () => [...sharePresales.packages].sort((a, b) => a.sıraNo - b.sıraNo || a.id - b.id),
    [sharePresales.packages]
  );

  const selectedPackage = useMemo(
    () => sharePresales.packages.find((item) => String(item.id) === selectedPackageId) ?? null,
    [selectedPackageId, sharePresales.packages]
  );

  const saleQuantity = Math.max(1, Number(quantity) || 1);
  const unitAmount = selectedPackage?.tutar ?? 0;
  const totalAmount = saleQuantity * unitAmount;
  const paidAmount = Math.max(0, parseMoneyInput(depositAmount));
  const remainingAmount = Math.max(0, totalAmount - paidAmount);

  const activeBankTransfers = useMemo(() => {
    const q = normalizeText(transferDescriptionFilter);
    return transfers
      .filter(isBankTransferActive)
      .filter((item) => dateInRange(item.tarih, transferDateFrom, transferDateTo))
      .filter((item) => {
        if (!q) return true;
        return [item.fisNo, item.aciklama, item.manualNote ?? ""].some((value) => normalizeText(value).includes(q));
      })
      .sort((a, b) => b.tarih.localeCompare(a.tarih) || Number(b.tutar || 0) - Number(a.tutar || 0));
  }, [transferDateFrom, transferDateTo, transferDescriptionFilter, transfers]);

  const summary = useMemo(() => {
    const activeSales = sharePresales.sales.filter((item) => item.durum !== "İptal");
    const shareCount = activeSales.reduce((sum, item) => sum + item.adet, 0);
    const linked = activeSales.reduce((sum, item) => sum + linkedShareCount(item), 0);
    return {
      saleCount: activeSales.length,
      shareCount,
      linked,
      unlinked: Math.max(0, shareCount - linked),
      total: activeSales.reduce((sum, item) => sum + item.toplamTutar, 0),
      paid: activeSales.reduce((sum, item) => sum + item.kaporaTutar, 0),
      remaining: activeSales.reduce((sum, item) => sum + item.kalanTutar, 0),
    };
  }, [sharePresales.sales]);

  const packageSummary = useMemo(() => {
    return allPackagesSorted.map((pkg) => {
      const matching = sharePresales.sales.filter((sale) => sale.durum !== "İptal" && sale.paketId === pkg.id);
      const sold = matching.reduce((sum, item) => sum + item.adet, 0);
      const linked = matching.reduce((sum, item) => sum + linkedShareCount(item), 0);
      return {
        id: pkg.id,
        sıraNo: pkg.sıraNo,
        title: pkg.baslik,
        type: pkg.hayvanTipi,
        amount: pkg.tutar,
        note: pkg.not ?? "",
        active: pkg.aktif,
        sold,
        linked,
        empty: Math.max(0, sold - linked),
      };
    });
  }, [allPackagesSorted, sharePresales.sales]);


  function moneyMatches(value: number, filter: string) {
    const q = filter.replace(/\D+/g, "");
    if (!q) return true;
    return String(Math.round(Number(value || 0))).includes(q);
  }

  function numberTextMatches(value: number, filter: string) {
    const q = filter.replace(/\D+/g, "");
    if (!q) return true;
    return String(Number(value || 0)).includes(q);
  }

  const filteredSales = useMemo(() => {
    const q = normalizeText(search);
    return [...sharePresales.sales]
      .filter((item) => statusFilter === "Hepsi" || item.durum === statusFilter)
      .filter((item) => packageFilter === "Hepsi" || item.paketId === packageFilter)
      .filter((item) => {
        if (!q) return true;
        return [item.satisNo, item.musteriAdi, item.telefon, item.paketBaslik, item.hayvanTipi, item.havaleNo ?? "", item.not ?? ""].some((value) => normalizeText(value).includes(q));
      })
      .filter((item) => {
        const qDateNo = normalizeText(dateNoFilter);
        if (!qDateNo) return true;
        return [item.tarih, formatTransferDate(item.tarih), item.satisNo].some((value) => normalizeText(value).includes(qDateNo));
      })
      .filter((item) => {
        const qCustomer = normalizeText(customerColumnFilter);
        if (!qCustomer) return true;
        return [item.musteriAdi, item.telefon].some((value) => normalizeText(value).includes(qCustomer));
      })
      .filter((item) => {
        const qPackage = normalizeText(packageColumnFilter);
        if (!qPackage) return true;
        return [item.paketBaslik, item.hayvanTipi].some((value) => normalizeText(value).includes(qPackage));
      })
      .filter((item) => {
        const qNote = normalizeText(saleNoteFilter);
        if (!qNote) return true;
        return normalizeText(item.not ?? "").includes(qNote);
      })
      .filter((item) => {
        const qKupe = normalizeText(kupeSiparisFilter);
        if (!qKupe) return true;
        return [item.linkedKupeNo ?? "", item.linkedSiparisNo ?? "", item.linkedHisseNo ?? ""].some((value) => normalizeText(value).includes(qKupe));
      })
      .filter((item) => {
        if (groupColumnFilter === "Hepsi") return true;
        if (groupColumnFilter === "Grupsuz") return !item.grupNo;
        return Number(item.grupNo) === Number(groupColumnFilter);
      })
      .filter((item) => {
        if (transportColumnFilter === "Hepsi") return true;
        return transportColumnFilter === "Var" ? Boolean(item.tasima) : !item.tasima;
      })
      .filter((item) => numberTextMatches(item.adet, adetFilter))
      .filter((item) => moneyMatches(item.toplamTutar, totalFilter))
      .filter((item) => moneyMatches(item.kaporaTutar, depositFilter))
      .filter((item) => moneyMatches(item.kalanTutar, remainingFilter))
      .filter((item) => paymentColumnFilter === "Hepsi" || (item.odemeTipi ?? "Nakit") === paymentColumnFilter)
      .sort((a, b) => b.tarih.localeCompare(a.tarih) || b.id - a.id);
  }, [
    adetFilter,
    customerColumnFilter,
    dateNoFilter,
    depositFilter,
    groupColumnFilter,
    kupeSiparisFilter,
    packageColumnFilter,
    packageFilter,
    paymentColumnFilter,
    remainingFilter,
    saleNoteFilter,
    search,
    sharePresales.sales,
    statusFilter,
    totalFilter,
    transportColumnFilter,
  ]);

  function getAnimalUsedSlots(animal: typeof animals[number]) {
    const used = new Set<number>();
    for (const share of animal.hisseler ?? []) {
      const rawSlots = Array.isArray(share.slotNos) && share.slotNos.length > 0
        ? share.slotNos
        : String(share.hisseNo ?? "").split("-").pop()?.split("+") ?? [];
      rawSlots.forEach((slot) => {
        const value = Number(String(slot).trim());
        if (Number.isFinite(value) && value > 0) used.add(value);
      });
    }
    return used;
  }

  function getAnimalFreeSlots(animal: typeof animals[number]) {
    const used = getAnimalUsedSlots(animal);
    const result: number[] = [];
    for (let i = 1; i <= Number(animal.toplamHisse || 0); i++) {
      if (!used.has(i)) result.push(i);
    }
    return result;
  }

  const selectedLinkSales = useMemo(() => {
    const ids = new Set(selectedSaleIds);
    return sharePresales.sales
      .filter((sale) => ids.has(sale.id) && sale.durum === "Bağlanmadı" && linkedShareCount(sale) === 0)
      .sort((a, b) => a.tarih.localeCompare(b.tarih) || a.id - b.id);
  }, [selectedSaleIds, sharePresales.sales]);

  const selectedNeededShareCount = selectedLinkSales.reduce((sum, sale) => sum + sale.adet, 0);

  const selectedRows = useMemo(() => {
    const ids = new Set(selectedSaleIds);
    return sharePresales.sales.filter((sale) => ids.has(sale.id));
  }, [selectedSaleIds, sharePresales.sales]);

  const selectedLinkedRows = selectedRows.filter((sale) => linkedShareCount(sale) > 0 || sale.durum !== "Bağlanmadı");
  const allFilteredSelected = filteredSales.length > 0 && filteredSales.every((sale) => selectedSaleIds.includes(sale.id));

  const linkableAnimals = useMemo(() => {
    const kupeNeedle = linkKupeSearch.trim();
    const siparisNeedle = linkSiparisSearch.trim();

    return [...animals]
      .map((animal) => ({ animal, freeSlots: getAnimalFreeSlots(animal) }))
      .filter(({ freeSlots }) => freeSlots.length > 0)
      .filter(({ animal }) => !kupeNeedle || String(animal.siraNo ?? "") === kupeNeedle || String(getDisplayKupeNo(animal)) === kupeNeedle)
      .filter(({ animal }) => !siparisNeedle || String(animal.kupeNo ?? "") === siparisNeedle || String(getDisplaySiparisNo(animal)) === siparisNeedle)
      .sort((a, b) => Number(a.animal.siraNo || 0) - Number(b.animal.siraNo || 0));
  }, [animals, linkKupeSearch, linkSiparisSearch]);

  const selectedLinkAnimal = useMemo(
    () => linkableAnimals.find((item) => item.animal.id === selectedLinkAnimalId) ?? null,
    [linkableAnimals, selectedLinkAnimalId]
  );
  const groupNumbers = useMemo(() => {
    const used = sharePresales.sales
      .map((sale) => Number(sale.grupNo))
      .filter((item) => Number.isFinite(item) && item > 0);
    return [...new Set([...manualGroupNumbers, ...used])].sort((a, b) => a - b);
  }, [manualGroupNumbers, sharePresales.sales]);

  function persistGroupNumbers(next: number[]) {
    const clean = [...new Set(next.filter((item) => Number.isFinite(item) && item > 0))].sort((a, b) => a - b);
    setManualGroupNumbers(clean);
    try {
      window.localStorage.setItem("evrenciftligi_share_presale_groups_v1", JSON.stringify(clean));
    } catch {
      // localStorage yazılamazsa sadece mevcut oturumda çalışır
    }
  }

  function addNewGroup() {
    const nextNo = Math.max(0, ...groupNumbers) + 1;
    persistGroupNumbers([...groupNumbers, nextNo]);
    setSelectedGroupNo(nextNo);
    setPendingGroupNo(nextNo);
    setMessage(`Grup ${nextNo} eklendi. Seçili kayıtları bu gruba almak için Kaydet'e bas.`);
  }

  const groupSummary = useMemo(() => {
    return groupNumbers.map((groupNo) => {
      const records = sharePresales.sales.filter((sale) => sale.durum !== "İptal" && sale.grupNo === groupNo);
      const shareCount = records.reduce((sum, sale) => sum + sale.adet, 0);
      const linked = records.reduce((sum, sale) => sum + linkedShareCount(sale), 0);
      return {
        groupNo,
        count: records.length,
        shareCount,
        linked,
        records,
      };
    });
  }, [sharePresales.sales]);

  async function assignSelectedSalesToGroup(groupNo: number) {
    if (selectedSaleIds.length === 0) return setMessage("Gruba bağlamak için en az bir ön satış seç.");
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const selectedIds = new Set(selectedSaleIds);
      await setSharePresales((prev) => ({
        ...prev,
        sales: prev.sales.map((sale) =>
          selectedIds.has(sale.id)
            ? { ...sale, grupNo: groupNo, updatedAt: now }
            : sale
        ),
      }));
      setSelectedGroupNo(groupNo);
      setGroupModalOpen(false);
      setMessage(`Seçili ${selectedSaleIds.length} ön satış Grup ${groupNo} içine alındı.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Gruba bağlama sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  }

  async function unlinkSelectedSalesFromGroup() {
    if (selectedSaleIds.length === 0) return setMessage("Grubu çözmek için en az bir ön satış seç.");
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const selectedIds = new Set(selectedSaleIds);
      await setSharePresales((prev) => ({
        ...prev,
        sales: prev.sales.map((sale) =>
          selectedIds.has(sale.id)
            ? { ...sale, grupNo: undefined, updatedAt: now }
            : sale
        ),
      }));
      setSelectedGroupNo(null);
      setMessage(`Seçili ${selectedSaleIds.length} ön satışın grup bağlantısı çözüldü.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Grubu çözme sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  }

  function selectGroupMembers(groupNo: number) {
    const ids = sharePresales.sales
      .filter((sale) => sale.durum !== "İptal" && sale.grupNo === groupNo)
      .map((sale) => sale.id);

    setSelectedGroupNo(groupNo);
    setPendingGroupNo(groupNo);

    if (ids.length > 0) {
      setSelectedSaleIds((prev) => Array.from(new Set([...prev, ...ids])));
      setMessage(`Grup ${groupNo} üyeleri seçime eklendi. İstersen + kişi ekleyip hayvana bağlayabilirsin.`);
    } else {
      setMessage(`Grup ${groupNo} seçildi. Seçili kayıtları bu gruba almak için "Bu Gruba Kaydet"e bas.`);
    }
  }

  async function clearGroup(groupNo: number) {
    const records = sharePresales.sales.filter((sale) => sale.grupNo === groupNo);
    if (records.length === 0) return setMessage(`Grup ${groupNo} zaten boş.`);
    const ok = window.confirm(`Grup ${groupNo} içindeki ${records.length} kayıt gruptan çıkarılsın mı?`);
    if (!ok) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      await setSharePresales((prev) => ({
        ...prev,
        sales: prev.sales.map((sale) =>
          sale.grupNo === groupNo ? { ...sale, grupNo: undefined, updatedAt: now } : sale
        ),
      }));
      setSelectedSaleIds((prev) => prev.filter((id) => !records.some((sale) => sale.id === id)));
      setMessage(`Grup ${groupNo} çözüldü.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Grup çözme sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  }

  async function deleteGroup(groupNo: number) {
    const records = sharePresales.sales.filter((sale) => sale.grupNo === groupNo);
    const ok = window.confirm(
      records.length > 0
        ? `Grup ${groupNo} silinsin mi? İçindeki ${records.length} kayıt gruptan çıkarılacak.`
        : `Boş Grup ${groupNo} silinsin mi?`
    );
    if (!ok) return;

    setSaving(true);
    try {
      const now = new Date().toISOString();
      if (records.length > 0) {
        await setSharePresales((prev) => ({
          ...prev,
          sales: prev.sales.map((sale) =>
            sale.grupNo === groupNo ? { ...sale, grupNo: undefined, updatedAt: now } : sale
          ),
        }));
      }
      persistGroupNumbers(groupNumbers.filter((item) => item !== groupNo));
      setSelectedSaleIds((prev) => prev.filter((id) => !records.some((sale) => sale.id === id)));
      if (selectedGroupNo === groupNo) setSelectedGroupNo(null);
      if (pendingGroupNo === groupNo) setPendingGroupNo(null);
      setMessage(`Grup ${groupNo} silindi.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Grup silme sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  }


  function toggleSaleSelection(sale: SharePresaleRecord) {
    setSelectedSaleIds((prev) =>
      prev.includes(sale.id) ? prev.filter((id) => id !== sale.id) : [...prev, sale.id]
    );
  }

  function toggleSelectFilteredSales() {
    const ids = filteredSales.map((sale) => sale.id);
    if (ids.length === 0) return;
    setSelectedSaleIds((prev) => {
      const allSelected = ids.every((id) => prev.includes(id));
      if (allSelected) return prev.filter((id) => !ids.includes(id));
      return Array.from(new Set([...prev, ...ids]));
    });
  }

  function openSingleLinkModal(sale: SharePresaleRecord) {
    setSelectedSaleIds([sale.id]);
    setSelectedLinkAnimalId(null);
    setLinkModalOpen(true);
  }

  async function linkSelectedSalesToAnimal() {
    if (selectedLinkSales.length === 0) return setMessage("Hayvana bağlamak için en az bir bağlanmamış ön satış seç.");
    if (!selectedLinkAnimal) return setMessage("Bağlanacak hayvan seç.");
    if (selectedLinkAnimal.freeSlots.length < selectedNeededShareCount) {
      return setMessage(`Seçili hayvanda yeterli boş hisse yok. Gerekli: ${selectedNeededShareCount}, boş: ${selectedLinkAnimal.freeSlots.length}`);
    }

    setSaving(true);
    try {
      await linkSharePresalesToAnimalBatchOperationRequest({
        saleIds: selectedLinkSales.map((sale) => sale.id),
        animalId: selectedLinkAnimal.animal.id,
      });
      await applyOperationResult();
      setSelectedSaleIds([]);
      setSelectedLinkAnimalId(null);
      setLinkModalOpen(false);
      setMessage(`${selectedLinkSales.length} ön satış ${selectedLinkAnimal.animal.kupeNo || selectedLinkAnimal.animal.siraNo} hayvanına bağlandı.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Seçili ön satışlar hayvana bağlanamadı.");
    } finally {
      setSaving(false);
    }
  }

  function resetPackageForm() {
    setEditingPackageId(null);
    setPackageTitle("");
    setPackageAnimalType("Dana");
    setPackageAmount("");
    setPackageNote("");
  }

  function resetSaleForm() {
    setSaleDate(today());
    setSelectedCustomerId("");
    setCustomerName("");
    setCustomerPhone("");
    setSelectedPackageId("");
    setQuantity("1");
    setDepositAmount("");
    setPaymentType("Nakit");
    setHavaleNo("");
    setSaleNote("");
    setSelectedTransferIds([]);
    setTransferPickerOpen(false);
  }

  function handleCustomerSelect(value: string) {
    setSelectedCustomerId(value);
    const customer = customers.find((item) => String(item.id) === value);
    if (!customer) return;
    setCustomerName(customer.adSoyad);
    setCustomerPhone(customer.telefon);
  }

  function startEditPackage(item: SharePresalePackage) {
    setEditingPackageId(item.id);
    setPackageTitle(item.baslik);
    setPackageAnimalType(item.hayvanTipi);
    setPackageAmount(String(item.tutar));
    setPackageNote(item.not ?? "");
    setMessage("Tanım düzenleme modunda. Değişiklikleri yaptıktan sonra Tanımı Güncelle butonuna bas.");
  }

  async function savePackage() {
    const amount = parseMoneyInput(packageAmount);
    const title = packageTitle.trim();

    if (!title) return setMessage("Hisse tanımı için başlık gir.");
    if (amount <= 0) return setMessage("Hisse tanımı için geçerli tutar gir.");

    setSaving(true);
    try {
      const now = new Date().toISOString();

      if (editingPackageId) {
        await setSharePresales((prev) => ({
          ...prev,
          packages: prev.packages.map((item) =>
            item.id === editingPackageId
              ? { ...item, baslik: title, hayvanTipi: packageAnimalType.trim() || "Dana", tutar: amount, not: packageNote.trim() || undefined, updatedAt: now }
              : item
          ),
        }));
        resetPackageForm();
        setMessage("Hisse tanımı güncellendi.");
        return;
      }

      const nextPackage: SharePresalePackage = {
        id: createClientNumericId(),
        sıraNo: sharePresales.packages.length + 1,
        baslik: title,
        hayvanTipi: packageAnimalType.trim() || "Dana",
        tutar: amount,
        aktif: true,
        not: packageNote.trim() || undefined,
        createdAt: now,
      };

      await setSharePresales((prev) => ({ ...prev, packages: [...prev.packages, nextPackage] }));
      resetPackageForm();
      setMessage("Hisse tanımı eklendi.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Hisse tanımı kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  }

  async function togglePackage(packageId: number) {
    await setSharePresales((prev) => ({
      ...prev,
      packages: prev.packages.map((item) => item.id === packageId ? { ...item, aktif: !item.aktif, updatedAt: new Date().toISOString() } : item),
    }));
  }

  async function deletePackage(packageId: number) {
    const packageSales = sharePresales.sales.filter((sale) => sale.paketId === packageId);

    if (packageSales.length > 0) {
      const ok = window.confirm("Bu tanıma bağlı satış var. Veri kaybı olmaması için silmek yerine pasife alacağız. Devam edilsin mi?");
      if (!ok) return;
      await setSharePresales((prev) => ({
        ...prev,
        packages: prev.packages.map((item) => item.id === packageId ? { ...item, aktif: false, updatedAt: new Date().toISOString() } : item),
      }));
      if (editingPackageId === packageId) resetPackageForm();
      setMessage("Tanıma bağlı satış olduğu için tanım silinmedi, pasife alındı.");
      return;
    }

    if (!window.confirm("Bu hisse tanımı kalıcı olarak silinsin mi?")) return;
    await setSharePresales((prev) => ({ ...prev, packages: prev.packages.filter((item) => item.id !== packageId) }));
    if (editingPackageId === packageId) resetPackageForm();
    setMessage("Hisse tanımı silindi.");
  }

  function toggleTransferSelection(id: string) {
    setSelectedTransferIds((prev) => prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]);
  }

  function addSelectedTransfersToSale() {
    const selected = activeBankTransfers.filter((item) => selectedTransferIds.includes(item.id));
    if (selected.length === 0) return setMessage("Bağlamak için en az bir aktif havale seç.");

    const totalCents = selected.reduce((sum, item) => sum + Math.round(Number(item.tutar || 0) * 100), 0);
    const total = Math.round(totalCents / 100);

    setPaymentType("Havale");
    setDepositAmount(String(total));
    setHavaleNo(selected.map((item) => item.fisNo).join(", "));
    setTransferPickerOpen(false);
    setMessage(`${selected.length} havale seçildi. Kapora tutarı seçili havale toplamına çekildi.`);
  }

  async function addSale() {
    if (!selectedPackage) return setMessage("Hisse satışı için tanım seç.");
    if (!customerName.trim()) return setMessage("Müşteri adı gir veya mevcut müşteriden seç.");
    if (!customerPhone.trim()) return setMessage("Telefon gir.");
    if (paidAmount > totalAmount) return setMessage("Kapora toplam tutardan büyük olamaz.");
    if (paidAmount > 0 && paymentType === "Havale" && !havaleNo.trim()) return setMessage("Havale kapora için dekont/fiş no gir veya aktif havale bağla.");

    const selectedTransferSet = new Set(selectedTransferIds);
    const missingTransfers = selectedTransferIds.filter((id) => !transfers.some((item) => item.id === id && isBankTransferActive(item)));
    if (paymentType === "Havale" && missingTransfers.length > 0) return setMessage("Seçili havalelerden biri artık aktif değil. Listeyi yenileyip tekrar seç.");

    setSaving(true);
    try {
      const now = new Date().toISOString();
      const selectedCustomer = customers.find((item) => String(item.id) === selectedCustomerId);
      const saleNo = makeSaleNo(sharePresales.sales.length);
      const nextSale: SharePresaleRecord = {
        id: createClientNumericId(),
        satisNo: saleNo,
        tarih: saleDate || today(),
        musteriId: selectedCustomer?.id,
        musteriAdi: customerName.trim(),
        telefon: customerPhone.trim(),
        paketId: selectedPackage.id,
        paketBaslik: selectedPackage.baslik,
        hayvanTipi: selectedPackage.hayvanTipi,
        adet: saleQuantity,
        birimTutar: selectedPackage.tutar,
        toplamTutar: totalAmount,
        kaporaTutar: paidAmount,
        kalanTutar: remainingAmount,
        odemeTipi: paymentType,
        havaleNo: paymentType === "Havale" ? havaleNo.trim() : undefined,
        linkedBankTransferIds: paymentType === "Havale" ? [...selectedTransferIds] : [],
        durum: "Bağlanmadı",
        not: saleNote.trim() || undefined,
        linkedAnimalIds: [],
        linkedShareIds: [],
        createdAt: now,
      };

      await setSharePresales((prev) => ({ ...prev, sales: [nextSale, ...prev.sales] }));

      if (paidAmount > 0) {
        const nextPayment: PaymentRecord = {
          id: createClientNumericId(),
          tarih: nextSale.tarih,
          musteriAdi: nextSale.musteriAdi,
          telefon: nextSale.telefon,
          musteriId: selectedCustomer?.id ?? 0,
          hayvanKupeNo: "Hisse Ön Satış",
          hayvanSiparisNo: "",
          animalId: 0,
          hisseNo: saleNo,
          shareId: 0,
          tutar: paidAmount,
          tip: paymentType,
          aciklama: "",
          havaleNo: paymentType === "Havale" ? havaleNo.trim() : undefined,
          fisNo: paymentType === "Havale" ? havaleNo.trim() : undefined,
          olusturan: "Hisse Satışları",
        };
        await setAllPayments((prev) => [nextPayment, ...prev]);
      }

      if (selectedTransferIds.length > 0) {
        await setAllBankTransfers((prev) =>
          prev.map((item) => {
            if (!selectedTransferSet.has(item.id)) return item;
            return { ...item, automaticFilled: true, manualFilled: false, manualNote: item.manualNote || "", updatedAt: now };
          })
        );
      }

      resetSaleForm();
      setMessage("Hisse ön satışı eklendi. Kapora alındıysa Ödemeler sayfasına da işlendi.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Hisse satışı kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  }

  async function updateSaleStatus(saleId: number, durum: SharePresaleStatus) {
    await setSharePresales((prev) => ({
      ...prev,
      sales: prev.sales.map((item) => item.id === saleId ? { ...item, durum, updatedAt: new Date().toISOString() } : item),
    }));
  }

  function markSaleLinked(sale: SharePresaleRecord) {
    if (sale.durum !== "Bağlanmadı" || linkedShareCount(sale) > 0) {
      setMessage("Bu ön satış zaten hayvana bağlı görünüyor. Önce Çöz işlemi yapılmalı.");
      return;
    }
    openSingleLinkModal(sale);
  }

  async function unlinkSaleFromAnimal(sale: SharePresaleRecord) {
    if (linkedShareCount(sale) <= 0 && sale.durum === "Bağlanmadı") return setMessage("Bu kayıt zaten boşta görünüyor.");
    if (!window.confirm("Bu ön satışın hayvan bağlantısı çözülsün mü? Ödeme/havale kaydı silinmez.")) return;

    setSaving(true);
    try {
      await unlinkSharePresaleFromAnimalOperationRequest(sale.id);
      await applyOperationResult();
      setMessage("Ön satış hayvandan çözüldü. Kapora/ödeme kaydı korundu.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Çözme sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  }

  async function removeSale(sale: SharePresaleRecord) {
    const ok = window.confirm("Bu hisse ön satış kaydı silinsin mi? Bağlı hayvan hissesi, ödeme ve havale bağlantısı da güvenli şekilde temizlenir.");
    if (!ok) return;

    setSaving(true);
    try {
      await deleteSharePresaleOperationRequest(sale.id);
      await applyOperationResult();
      setMessage("Hisse ön satış kaydı silindi.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Hisse ön satış silinemedi.");
    } finally {
      setSaving(false);
    }
  }

  async function unlinkSelectedFromAnimals() {
    const targets = selectedRows.filter((sale) => linkedShareCount(sale) > 0 || sale.durum !== "Bağlanmadı");
    if (targets.length === 0) return setMessage("Çözülecek bağlı ön satış seçili değil.");
    const ok = window.confirm(`Seçili ${targets.length} ön satışın hayvan bağlantısı çözülsün mü? Ödeme/havale kayıtları silinmez.`);
    if (!ok) return;

    setSaving(true);
    try {
      for (const sale of targets) {
        await unlinkSharePresaleFromAnimalOperationRequest(sale.id);
      }
      await applyOperationResult();
      setSelectedSaleIds([]);
      setMessage(`${targets.length} ön satış hayvandan çözüldü.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Toplu çözme sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  }

  function exportSharePresales() {
    downloadSharePresaleWorkbook(filteredSales, {
      search,
      kupeNo: kupeSiparisFilter,
      customer: customerColumnFilter,
      user: "",
      salesType: "Tümü",
      paymentStatus: "Tümü",
      noteMode: saleNoteFilter ? "Not Var" : "Tümü",
      dateFrom: "",
      dateTo: "",
      minSale: totalFilter,
      maxSale: "",
      minRemaining: remainingFilter,
      maxRemaining: "",
      preset: "all",
    });
  }

  async function handleAddCustomer(data: UserFormData) {
    const nextCustomerId = customers.length > 0 ? Math.max(...customers.map((item) => item.id)) + 1 : 1;
    const initialPassword = data.sifre?.trim() || "Abc1234";

    await setAllCustomers((prev) => [
      ...prev,
      {
        id: nextCustomerId,
        adSoyad: data.adSoyad,
        telefon: data.telefon,
        kisaNot: data.kisaNot || undefined,
        toplamHisseSayisi: 0,
        toplamBorc: 0,
        toplamOdenen: 0,
      },
    ]);

    await setAllUsers((prev) => [
      {
        id: getNextUserId(),
        adSoyad: data.adSoyad,
        telefon: data.telefon,
        email: data.email,
        rol: "Müşteri",
        durum: data.durum,
        eklemeTarihi: new Date().toISOString().slice(0, 10),
        sonGiris: undefined,
        sifre: initialPassword,
        customerId: nextCustomerId,
        forcePasswordChange: true,
        smsOperationalOptIn: data.smsOperationalOptIn,
        smsMarketingOptIn: data.smsMarketingOptIn,
      },
      ...prev,
    ]);

    void sendSmsByTemplate({
      templateKey: "welcomeTempPassword",
      channel: "auth",
      eventType: "welcome_temp_password",
      phone: data.telefon,
      customerId: nextCustomerId,
      variables: {
        customerName: data.adSoyad,
        password: initialPassword,
      },
    });

    setShowCustomerModal(false);
    setMessage("Yeni müşteri eklendi.");
  }


  return (
    <main className="h-full overflow-y-auto bg-farm-cream p-4 lg:p-6">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="font-body text-xs font-semibold uppercase tracking-wide text-farm-earth">Ön Planlama / Broşür Satışı</p>
          <h1 className="font-heading text-2xl font-bold text-farm-brown">Hisse Satışları</h1>
          <p className="mt-1 max-w-3xl font-body text-xs text-farm-muted">
            Hayvandan bağımsız ön hisse satışı. Kapora alındığında Ödemeler sayfasına hisse ön satış kaporası olarak yansır.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row lg:flex-col">
          <button type="button" onClick={() => setSaleDrawerOpen(true)} className="rounded-full bg-farm-earth px-4 py-2 text-xs font-bold text-white hover:bg-farm-brown">
            <i className="ri-add-line mr-1" /> Hisse Satışı Ekle
          </button>
          <button type="button" onClick={() => setShowCustomerModal(true)} className="rounded-full border border-farm-border bg-white px-4 py-2 text-xs font-bold text-farm-brown hover:border-farm-earth">
            <i className="ri-user-add-line mr-1" /> Yeni Müşteri
          </button>
        </div>
      </div>

      {message && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-farm border border-farm-earth/20 bg-white px-4 py-3 font-body text-xs text-farm-brown shadow-sm">
          <span>{message}</span>
          <button type="button" onClick={() => setMessage(null)} className="text-farm-muted hover:text-farm-brown"><i className="ri-close-line text-base" /></button>
        </div>
      )}

      <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-7">
        <StatCard label="Ön Satış" value={String(summary.saleCount)} sub="İptal hariç kayıt" />
        <StatCard label="Toplam Hisse" value={String(summary.shareCount)} sub="Satılan adet" />
        <StatCard label="Bağlanan" value={String(summary.linked)} sub="Hayvana bağlanan" />
        <StatCard label="Boşta" value={String(summary.unlinked)} sub="Hayvana atanacak" />
        <StatCard label="Satış Tutarı" value={formatTry(summary.total)} />
        <StatCard label="Kapora" value={formatTry(summary.paid)} />
        <StatCard label="Kalan" value={formatTry(summary.remaining)} />
      </section>

      <div className="mt-4 rounded-farm border border-farm-border bg-white px-3 py-2 shadow-sm">
        <button
          type="button"
          onClick={() => setDefinitionsPanelOpen((prev) => !prev)}
          className="flex w-full items-center justify-between gap-3 text-left"
        >
          <div>
            <h2 className="font-heading text-base font-bold text-farm-brown">Hisse Tanımları / Tanım Detayları</h2>
            <p className="font-body text-[11px] text-farm-muted">Tanım ekleme, düzenleme, silme, aktif-pasif ve paket süzme alanı.</p>
          </div>
          <span className="flex h-8 w-8 items-center justify-center rounded-full border border-farm-border bg-farm-cream text-farm-brown">
            <i className={`${definitionsPanelOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-lg`} />
          </span>
        </button>
      </div>

      {definitionsPanelOpen && (
      <section className="mt-3 grid gap-4 xl:grid-cols-[380px_minmax(0,1fr)]">
        <div className="rounded-farm border border-farm-border bg-white p-3 shadow-sm">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div>
              <h2 className="font-heading text-base font-bold text-farm-brown">Hisse Tanımları</h2>
              <p className="font-body text-[11px] text-farm-muted">Yeni tanım ekle veya seçili tanımı düzenle.</p>
            </div>
            {editingPackageId ? <span className="rounded-full bg-amber-50 px-2 py-1 text-[10px] font-bold text-amber-700">Düzenleme</span> : null}
          </div>

          <div className="space-y-2 rounded-farm border border-farm-border bg-farm-cream p-2">
            <input value={packageTitle} onChange={(e) => setPackageTitle(e.target.value)} placeholder="Paket / açıklama" className="h-9 w-full rounded-farm border border-farm-border bg-white px-3 font-body text-xs text-farm-brown outline-none focus:border-farm-earth" />
            <div className="grid grid-cols-2 gap-2">
              <input value={packageAnimalType} onChange={(e) => setPackageAnimalType(e.target.value)} placeholder="Dana / Düve" className="h-9 rounded-farm border border-farm-border bg-white px-3 font-body text-xs text-farm-brown outline-none focus:border-farm-earth" />
              <MoneyInput value={packageAmount} onChange={setPackageAmount} placeholder="33000" className="bg-white" />
            </div>
            <input value={packageNote} onChange={(e) => setPackageNote(e.target.value)} placeholder="Not" className="h-9 w-full rounded-farm border border-farm-border bg-white px-3 font-body text-xs text-farm-brown outline-none focus:border-farm-earth" />
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={savePackage} disabled={saving} className="h-9 rounded-farm bg-farm-brown px-3 font-body text-xs font-bold text-white transition hover:bg-farm-earth disabled:opacity-60">
                {editingPackageId ? "Güncelle" : "Ekle"}
              </button>
              <button type="button" onClick={resetPackageForm} className="h-9 rounded-farm border border-farm-border bg-white px-3 font-body text-xs font-bold text-farm-brown transition hover:border-farm-earth">Vazgeç</button>
            </div>
          </div>

          <div className="mt-3 rounded-farm border border-dashed border-farm-border bg-farm-cream px-3 py-2 font-body text-[11px] text-farm-muted">
            Tanımların listesi, düzenleme, silme ve aktif-pasif işlemleri sağdaki <b>Tanım Detayları</b> tablosundan yapılır.
          </div>
        </div>

        <div className="rounded-farm border border-farm-border bg-white p-3 shadow-sm">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div>
              <h2 className="font-heading text-base font-bold text-farm-brown">Tanım Detayları</h2>
              <p className="font-body text-[11px] text-farm-muted">Satıra basınca alttaki liste o pakete süzülür.</p>
            </div>
            <button type="button" onClick={() => setPackageFilter("Hepsi")} className="rounded-full border border-farm-border bg-farm-cream px-3 py-1 text-[11px] font-bold text-farm-brown">Hepsi</button>
          </div>

          <div className="overflow-x-auto rounded-farm border border-farm-border">
            <table className="w-full min-w-[980px] border-collapse text-left font-body text-xs">
              <thead className="bg-farm-warm text-[10px] uppercase tracking-wide text-farm-muted">
                <tr>
                  <th className="px-2 py-1.5">Paket / Açıklama</th>
                  <th className="px-2 py-1.5">Tutar</th>
                  <th className="px-2 py-1.5">Not</th>
                  <th className="px-2 py-1.5 text-center">Satıldı</th>
                  <th className="px-2 py-1.5 text-center">Bağlı</th>
                  <th className="px-2 py-1.5 text-center">Boşta</th>
                  <th className="px-2 py-1.5 text-center">Durum</th>
                  <th className="px-2 py-1.5 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {packageSummary.length === 0 ? (
                  <tr><td colSpan={8} className="px-3 py-5 text-center text-farm-muted">Tanım yok.</td></tr>
                ) : packageSummary.map((item) => {
                  const packageItem = allPackagesSorted.find((pkg) => pkg.id === item.id);
                  return (
                  <tr key={item.id} onClick={() => setPackageFilter(item.id)} className={`cursor-pointer border-t border-farm-border hover:bg-farm-cream ${packageFilter === item.id ? "bg-farm-gold-pale/40" : "bg-white"}`}>
                    <td className="px-2 py-1.5">
                      <div className="font-bold text-farm-brown">{item.sıraNo}. {item.title}</div>
                      <div className="text-[10px] text-farm-muted">{item.type}</div>
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 font-bold text-farm-green">{formatTry(item.amount)}</td>
                    <td className="max-w-[220px] truncate px-2 py-1.5 text-[11px] text-farm-muted" title={item.note || "—"}>{item.note || "—"}</td>
                    <td className="px-2 py-1.5 text-center font-bold text-farm-brown">{item.sold}</td>
                    <td className="px-2 py-1.5 text-center font-bold text-emerald-700">{item.linked}</td>
                    <td className="px-2 py-1.5 text-center font-bold text-amber-700">{item.empty}</td>
                    <td className="px-2 py-1.5 text-center">
                      <button
                        type="button"
                        onClick={(event) => { event.stopPropagation(); togglePackage(item.id); }}
                        className={`rounded-full border px-2 py-0.5 font-body text-[10px] font-bold ${item.active ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-farm-border bg-white text-farm-muted"}`}
                      >
                        {item.active ? "Aktif" : "Pasif"}
                      </button>
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <div className="flex justify-end gap-1 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={(event) => { event.stopPropagation(); if (packageItem) startEditPackage(packageItem); }}
                          className="rounded-lg border border-farm-border bg-white px-2 py-1 text-[10px] font-semibold text-farm-brown hover:border-farm-earth"
                        >
                          Düzenle
                        </button>
                        <button
                          type="button"
                          onClick={(event) => { event.stopPropagation(); deletePackage(item.id); }}
                          className="rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-[10px] font-semibold text-red-600 hover:bg-red-500 hover:text-white"
                        >
                          Sil
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>
      )}

      <section className="mt-4 space-y-4">
        <div className="rounded-farm border border-dashed border-farm-border bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="font-heading text-lg font-bold text-farm-brown">Hisse Satışı Ekle</h2>
              <p className="font-body text-xs text-farm-muted">Yeni kayıt artık sağdan açılan satış balonu ile girilir.</p>
            </div>
            <button type="button" onClick={() => setSaleDrawerOpen(true)} className="rounded-full bg-farm-earth px-4 py-2 text-xs font-bold text-white hover:bg-farm-brown">
              <i className="ri-add-line mr-1" /> Hisse Satışı Ekle
            </button>
          </div>
        </div>

        <div className="rounded-farm border border-farm-border bg-white p-4 shadow-sm">
          <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="font-heading text-lg font-bold text-farm-brown">Hisse Ön Satış Listesi</h2>
              <p className="font-body text-xs text-farm-muted">Ön satış, kapora, grup, taşıma ve hayvan bağlantı durumu.</p>
              {selectedSaleIds.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-farm-cream px-3 py-1 text-[11px] font-bold text-farm-brown">{selectedLinkSales.length} kayıt / {selectedNeededShareCount} hisse seçildi</span>
                  <button type="button" onClick={() => { setSelectedLinkAnimalId(null); setLinkModalOpen(true); }} className="rounded-full bg-emerald-600 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-emerald-700">Hayvana Bağla</button>
                  <button type="button" onClick={() => { setSelectedGroupNo(null); setPendingGroupNo(null); setGroupModalOpen(true); }} className="rounded-full bg-farm-earth px-3 py-1.5 text-[11px] font-bold text-white hover:bg-farm-brown">Grupla</button>
                  <button type="button" onClick={unlinkSelectedFromAnimals} className="rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-[11px] font-bold text-red-600 hover:bg-red-500 hover:text-white">Seçili Hayvan Bağını Çöz</button>
                  <button type="button" onClick={unlinkSelectedSalesFromGroup} className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] font-bold text-amber-700 hover:bg-amber-500 hover:text-white">Grubu Çöz</button>
                  <button type="button" onClick={() => setSelectedSaleIds([])} className="rounded-full border border-farm-border bg-white px-3 py-1.5 text-[11px] font-bold text-farm-muted hover:border-farm-earth">Seçimi Temizle</button>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Müşteri / telefon / paket ara" className="h-10 rounded-farm border border-farm-border bg-farm-cream px-3 font-body text-xs text-farm-brown outline-none focus:border-farm-earth" />
              <select value={packageFilter} onChange={(e) => setPackageFilter(e.target.value === "Hepsi" ? "Hepsi" : Number(e.target.value))} className="h-10 rounded-farm border border-farm-border bg-farm-cream px-3 font-body text-xs text-farm-brown outline-none focus:border-farm-earth">
                <option value="Hepsi">Paket: Hepsi</option>
                {allPackagesSorted.map((item) => <option key={item.id} value={item.id}>{item.sıraNo}. {item.baslik}</option>)}
              </select>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as SharePresaleStatus | "Hepsi")} className="h-10 rounded-farm border border-farm-border bg-farm-cream px-3 font-body text-xs text-farm-brown outline-none focus:border-farm-earth">
                {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status}</option>)}
              </select>
              <button type="button" onClick={exportSharePresales} className="h-10 rounded-farm border border-farm-earth/30 bg-white px-3 font-body text-xs font-bold text-farm-brown hover:border-farm-earth">
                Excel Dökümü
              </button>
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setDateNoFilter("");
                  setCustomerColumnFilter("");
                  setPackageColumnFilter("");
                  setKupeSiparisFilter("");
                  setSaleNoteFilter("");
                  setGroupColumnFilter("Hepsi");
                  setTransportColumnFilter("Hepsi");
                  setAdetFilter("");
                  setTotalFilter("");
                  setDepositFilter("");
                  setRemainingFilter("");
                  setPaymentColumnFilter("Hepsi");
                  setStatusFilter("Hepsi");
                  setPackageFilter("Hepsi");
                }}
                className="h-10 rounded-farm border border-farm-border bg-white px-3 font-body text-xs font-bold text-farm-muted hover:border-farm-earth"
              >
                Filtreleri Temizle
              </button>
            </div>
          </div>

          <div className="overflow-x-auto rounded-farm border border-farm-border">
            <table className="share-presale-table border-collapse bg-white text-left">
              <colgroup>
                <col style={{ width: 38 }} />
                <col style={{ width: 88 }} />
                <col style={{ width: 176 }} />
                <col style={{ width: 136 }} />
                <col />
                <col style={{ width: 80 }} />
                <col style={{ width: 68 }} />
                <col style={{ width: 52 }} />
                <col style={{ width: 44 }} />
                <col style={{ width: 86 }} />
                <col style={{ width: 86 }} />
                <col style={{ width: 86 }} />
                <col style={{ width: 64 }} />
                <col style={{ width: 101 }} />
                <col style={{ width: 160 }} />
              </colgroup>
              <thead className="bg-farm-warm text-farm-brown">
                <tr className="font-body text-[10px] uppercase tracking-wide text-farm-muted">
                  <th className="px-3 py-2 text-center">
                    <div className="flex flex-col items-center gap-1">
                      <span>Seç</span>
                      <button type="button" onClick={toggleSelectFilteredSales} className={`flex h-5 w-5 items-center justify-center rounded border ${allFilteredSelected ? "border-emerald-600 bg-emerald-600 text-white" : "border-farm-border bg-white text-transparent"}`} title="Filtredeki tüm satırları seç">
                        <i className="ri-check-line text-xs" />
                      </button>
                    </div>
                  </th>
                  <th className="px-3 py-2">Tarih / No</th>
                  <th className="px-3 py-2">Müşteri</th>
                  <th className="px-3 py-2">Paket</th>
                  <th className="px-3 py-2">Not</th>
                  <th className="px-3 py-2">Küpe / Sipariş</th>
                  <th className="px-3 py-2 text-center">Grup</th>
                  <th className="px-3 py-2 text-center">Taşıma</th>
                  <th className="px-3 py-2 text-center">Adet</th>
                  <th className="px-3 py-2 text-right">Toplam</th>
                  <th className="px-3 py-2 text-right">Kapora</th>
                  <th className="px-3 py-2 text-right">Kalan</th>
                  <th className="px-3 py-2">Ödeme</th>
                  <th className="px-3 py-2">Durum</th>
                  <th className="pl-6 pr-3 py-2 text-right">İşlem</th>
                </tr>
                <tr className="border-t border-farm-border bg-white text-[10px] text-farm-muted">
                  <th className="px-2 py-1 text-center">—</th>
                  <th className="px-2 py-1"><input value={dateNoFilter} onChange={(e) => setDateNoFilter(e.target.value)} placeholder="Tarih / no" className="h-8 w-full rounded border border-farm-border bg-farm-cream px-2 text-[11px] outline-none focus:border-farm-earth" /></th>
                  <th className="px-2 py-1"><input value={customerColumnFilter} onChange={(e) => setCustomerColumnFilter(e.target.value)} placeholder="Müşteri / tel" className="h-8 w-full rounded border border-farm-border bg-farm-cream px-2 text-[11px] outline-none focus:border-farm-earth" /></th>
                  <th className="px-2 py-1"><input value={packageColumnFilter} onChange={(e) => setPackageColumnFilter(e.target.value)} placeholder="Paket" className="h-8 w-full rounded border border-farm-border bg-farm-cream px-2 text-[11px] outline-none focus:border-farm-earth" /></th>
                  <th className="px-2 py-1"><input value={saleNoteFilter} onChange={(e) => setSaleNoteFilter(e.target.value)} placeholder="Not" className="h-8 w-full rounded border border-farm-border bg-farm-cream px-2 text-[11px] outline-none focus:border-farm-earth" /></th>
                  <th className="px-2 py-1"><input value={kupeSiparisFilter} onChange={(e) => setKupeSiparisFilter(e.target.value)} placeholder="Küpe / sipariş" className="h-8 w-full rounded border border-farm-border bg-farm-cream px-2 text-[11px] outline-none focus:border-farm-earth" /></th>
                  <th className="px-2 py-1">
                    <select value={groupColumnFilter} onChange={(e) => setGroupColumnFilter(e.target.value === "Hepsi" || e.target.value === "Grupsuz" ? e.target.value : Number(e.target.value))} className="h-8 w-full rounded border border-farm-border bg-farm-cream px-2 text-[11px] outline-none focus:border-farm-earth">
                      <option value="Hepsi">Hepsi</option>
                      <option value="Grupsuz">Grupsuz</option>
                      {groupNumbers.map((groupNo) => <option key={groupNo} value={groupNo}>Grup {groupNo}</option>)}
                    </select>
                  </th>
                  <th className="px-2 py-1">
                    <select value={transportColumnFilter} onChange={(e) => setTransportColumnFilter(e.target.value as "Hepsi" | "Var" | "Yok")} className="h-8 w-full rounded border border-farm-border bg-farm-cream px-2 text-[11px] outline-none focus:border-farm-earth">
                      <option value="Hepsi">Hepsi</option>
                      <option value="Var">Var</option>
                      <option value="Yok">Yok</option>
                    </select>
                  </th>
                  <th className="px-2 py-1"><input value={adetFilter} onChange={(e) => setAdetFilter(e.target.value)} placeholder="Adet" className="h-8 w-full rounded border border-farm-border bg-farm-cream px-2 text-center text-[11px] outline-none focus:border-farm-earth" /></th>
                  <th className="px-2 py-1"><input value={totalFilter} onChange={(e) => setTotalFilter(e.target.value)} placeholder="Toplam" className="h-8 w-full rounded border border-farm-border bg-farm-cream px-2 text-right text-[11px] outline-none focus:border-farm-earth" /></th>
                  <th className="px-2 py-1"><input value={depositFilter} onChange={(e) => setDepositFilter(e.target.value)} placeholder="Kapora" className="h-8 w-full rounded border border-farm-border bg-farm-cream px-2 text-right text-[11px] outline-none focus:border-farm-earth" /></th>
                  <th className="px-2 py-1"><input value={remainingFilter} onChange={(e) => setRemainingFilter(e.target.value)} placeholder="Kalan" className="h-8 w-full rounded border border-farm-border bg-farm-cream px-2 text-right text-[11px] outline-none focus:border-farm-earth" /></th>
                  <th className="px-2 py-1">
                    <select value={paymentColumnFilter} onChange={(e) => setPaymentColumnFilter(e.target.value as "Hepsi" | "Nakit" | "Havale")} className="h-8 w-full rounded border border-farm-border bg-farm-cream px-2 text-[11px] outline-none focus:border-farm-earth">
                      <option value="Hepsi">Hepsi</option>
                      <option value="Nakit">Nakit</option>
                      <option value="Havale">Havale</option>
                    </select>
                  </th>
                  <th className="px-2 py-1">
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as SharePresaleStatus | "Hepsi")} className="h-8 w-full rounded border border-farm-border bg-farm-cream px-2 text-[11px] outline-none focus:border-farm-earth">
                      {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status}</option>)}
                    </select>
                  </th>
                  <th className="pl-6 pr-2 py-1 text-right">—</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-farm-border font-body text-xs">
                {filteredSales.length === 0 ? (
                  <tr><td colSpan={15} className="px-3 py-8 text-center text-farm-muted">Kayıt yok.</td></tr>
                ) : filteredSales.map((sale) => {
                  const linked = linkedShareCount(sale);
                  return (
                    <tr key={sale.id} className="hover:bg-farm-cream/60">
                      <td className="px-3 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => toggleSaleSelection(sale)}
                          className={`mx-auto flex h-5 w-5 items-center justify-center rounded border ${selectedSaleIds.includes(sale.id) ? "border-emerald-600 bg-emerald-600 text-white" : "border-farm-border bg-white text-transparent"}`}
                          title="Seç"
                        >
                          <i className="ri-check-line text-xs" />
                        </button>
                      </td>
                      <td className="px-3 py-2"><div className="font-mono text-farm-muted">{formatTransferDate(sale.tarih)}</div><div className="truncate font-mono text-[10px] text-farm-earth" title={sale.satisNo}>{sale.satisNo}</div></td>
                      <td className="px-3 py-2"><div className="truncate font-bold text-farm-brown" title={sale.musteriAdi}>{sale.musteriAdi}</div><div className="truncate font-mono text-[10px] text-farm-muted" title={sale.telefon}>{sale.telefon}</div></td>
                      <td className="px-3 py-2"><div className="truncate font-bold text-farm-brown" title={sale.paketBaslik}>{sale.paketBaslik}</div><div className="truncate text-[10px] text-farm-muted" title={`${sale.hayvanTipi} · ${formatTry(sale.birimTutar)}`}>{sale.hayvanTipi} · {formatTry(sale.birimTutar)}</div></td>
                      <td className="px-3 py-2 text-[11px] text-farm-muted"><span className="line-clamp-2" title={sale.not || "—"}>{sale.not || "—"}</span></td>
                      <td className="px-3 py-2"><div className="truncate font-mono text-[10px] font-bold text-farm-brown" title={sale.linkedKupeNo || "—"}>{sale.linkedKupeNo || "—"}</div><div className="truncate font-mono text-[10px] text-farm-muted" title={sale.linkedSiparisNo || "—"}>{sale.linkedSiparisNo || "—"}</div></td>
                      <td className="px-3 py-2 text-center">{sale.grupNo ? <span className="inline-flex whitespace-nowrap rounded-full bg-farm-earth-pale px-2 py-1 text-[10px] font-bold text-farm-brown">Grup {sale.grupNo}</span> : <span className="text-[10px] text-farm-muted">—</span>}</td>
                      <td className="px-3 py-2 text-center">{sale.tasima ? <span className="rounded-full bg-sky-50 px-2 py-1 text-[10px] font-bold text-sky-700">Var</span> : <span className="rounded-full bg-gray-50 px-2 py-1 text-[10px] font-bold text-gray-500">Yok</span>}</td>
                      <td className="px-3 py-2 text-center"><div className="font-bold text-farm-brown">{sale.adet}</div><div className="text-[10px] text-farm-muted">Bağlı {linked}</div></td>
                      <td className="px-3 py-2 text-right font-bold text-farm-brown">{formatTry(sale.toplamTutar)}</td>
                      <td className="px-3 py-2 text-right font-bold text-farm-green">{formatTry(sale.kaporaTutar)}</td>
                      <td className="px-3 py-2 text-right font-bold text-amber-700">{formatTry(sale.kalanTutar)}</td>
                      <td className="px-3 py-2"><div className={`inline-flex rounded-full border px-2 py-1 text-[10px] font-bold ${sale.odemeTipi === "Havale" ? "border-sky-200 bg-sky-50 text-sky-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>{sale.odemeTipi ?? "Nakit"}</div>{sale.havaleNo && <div className="mt-1 truncate text-[10px] text-farm-muted" title={sale.havaleNo}>{sale.havaleNo}</div>}</td>
                      <td className="px-3 py-2"><select value={sale.durum} onChange={(e) => updateSaleStatus(sale.id, e.target.value as SharePresaleStatus)} className={`h-8 rounded-full border px-2 text-[10px] font-semibold outline-none ${statusBadgeClass(sale.durum)}`}>{STATUS_OPTIONS.filter((item) => item !== "Hepsi").map((status) => <option key={status} value={status}>{status}</option>)}</select></td>
                      <td className="pl-6 pr-3 py-2 text-right">
                        <div className="flex justify-end gap-1 whitespace-nowrap">
                          <button type="button" onClick={() => setEditingSale(sale)} title="Düzenle" className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-farm-border bg-white text-farm-brown hover:border-farm-earth">
                            <i className="ri-edit-line text-sm" />
                          </button>
                          <button type="button" onClick={() => markSaleLinked(sale)} title="Hayvana bağla" className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-600 hover:text-white">
                            <i className="ri-link text-sm" />
                          </button>
                          <button type="button" onClick={() => unlinkSaleFromAnimal(sale)} title="Hayvan bağını çöz" className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-500 hover:text-white">
                            <i className="ri-link-unlink text-sm" />
                          </button>
                          <button type="button" onClick={() => removeSale(sale)} title="Sil" className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-red-200 bg-red-50 text-red-600 hover:bg-red-500 hover:text-white">
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {showCustomerModal && (
        <UserModal
          mode="add"
          forceRole="Müşteri"
          titleOverride="Yeni Müşteri Ekle"
          subtitleOverride="Hisse satışları için yeni müşteri kaydı"
          onClose={() => setShowCustomerModal(false)}
          onSave={handleAddCustomer}
        />
      )}

      {saleDrawerOpen && <HisseSatisEkleDrawer onClose={() => setSaleDrawerOpen(false)} />}
      {editingSale && <HisseSatisEkleDrawer editingSale={editingSale} onClose={() => setEditingSale(null)} />}


      {groupModalOpen && (
        <div className="fixed inset-0 z-[84] flex items-center justify-center bg-black/40 px-3">
          <div className="w-full max-w-4xl rounded-farm bg-white p-4 shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b border-farm-border pb-3">
              <div>
                <h3 className="font-heading text-lg font-bold text-farm-brown">Seçili Ön Satışları Grupla</h3>
                <p className="font-body text-xs text-farm-muted">Grup sadece Hisse Ön Satışları sayfasında kullanılır. Hayvana bağlamadan önce birbirini tanıyan kişileri bir arada tutar.</p>
              </div>
              <button type="button" onClick={() => setGroupModalOpen(false)} className="h-8 w-8 rounded-full text-farm-muted hover:bg-farm-cream"><i className="ri-close-line" /></button>
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
              <div className="rounded-farm border border-farm-border bg-farm-cream p-3">
                <p className="font-heading text-sm font-bold text-farm-brown">Seçilenler</p>
                <p className="mb-2 text-[11px] text-farm-muted">{selectedSaleIds.length} kayıt seçili</p>
                <div className="max-h-[320px] space-y-2 overflow-y-auto pr-1">
                  {sharePresales.sales.filter((sale) => selectedSaleIds.includes(sale.id)).length === 0 ? (
                    <div className="rounded-farm border border-dashed border-farm-border bg-white px-3 py-8 text-center text-xs text-farm-muted">Önce soldaki kutucuklardan kişi seç.</div>
                  ) : sharePresales.sales.filter((sale) => selectedSaleIds.includes(sale.id)).map((sale) => (
                    <div key={sale.id} className="rounded-farm border border-farm-border bg-white px-3 py-2">
                      <div className="text-xs font-bold text-farm-brown">{sale.musteriAdi}</div>
                      <div className="text-[10px] text-farm-muted">{sale.paketBaslik} · {sale.adet} hisse · {sale.grupNo ? `Grup ${sale.grupNo}` : "Grupsuz"}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-farm border border-farm-border bg-white p-3">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div>
                    <p className="font-heading text-sm font-bold text-farm-brown">Mevcut Gruplar / Grup Ekle</p>
                    <p className="text-[11px] text-farm-muted">Grup satırında “Bu Gruba Kaydet”e basarak seçili müşterileri gruba al. Üyeleri Seç, sadece o grubun mevcut üyelerini seçime ekler.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={unlinkSelectedSalesFromGroup} className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] font-bold text-amber-700 hover:bg-amber-500 hover:text-white">Seçili Grubu Çöz</button>
                    <button type="button" onClick={addNewGroup} className="rounded-full bg-farm-earth px-3 py-1.5 text-[11px] font-bold text-white hover:bg-farm-brown">Yeni Grup Ekle</button>
                  </div>
                </div>

                <div className="space-y-2">
                  {groupSummary.map((group) => (
                    <div
                      key={group.groupNo}
                      className={`grid grid-cols-[1fr_auto_auto_auto] items-center gap-2 rounded-farm border px-3 py-2 ${
                        (pendingGroupNo ?? selectedGroupNo) === group.groupNo
                          ? "border-farm-earth bg-farm-earth-pale"
                          : "border-farm-border bg-farm-cream"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => { setSelectedGroupNo(group.groupNo); setPendingGroupNo(group.groupNo); }}
                        className="min-w-0 text-left"
                      >
                        <div className="font-heading text-sm font-bold text-farm-brown">Grup {group.groupNo}</div>
                        <div className="truncate text-[11px] text-farm-muted">
                          {group.count} kayıt · {group.shareCount} hisse · {group.linked} bağlı
                        </div>
                      </button>
                      <button
                        type="button"
                        disabled={selectedSaleIds.length === 0 || saving}
                        onClick={() => assignSelectedSalesToGroup(group.groupNo)}
                        className="rounded-full bg-farm-earth px-3 py-1.5 text-[10px] font-bold text-white hover:bg-farm-brown disabled:cursor-not-allowed disabled:opacity-45"
                      >
                        Bu Gruba Kaydet
                      </button>
                      <button
                        type="button"
                        onClick={() => selectGroupMembers(group.groupNo)}
                        className="rounded-full border border-farm-border bg-white px-3 py-1.5 text-[10px] font-bold text-farm-brown hover:border-farm-earth"
                      >
                        Üyeleri Seç
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteGroup(group.groupNo)}
                        className="rounded-full border border-red-200 bg-white px-3 py-1.5 text-[10px] font-bold text-red-600 hover:bg-red-500 hover:text-white"
                      >
                        Sil
                      </button>
                    </div>
                  ))}
                </div>

                <div className="mt-4 flex flex-col gap-2 border-t border-farm-border pt-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-[11px] text-farm-muted">
                    {pendingGroupNo ? `Seçili ${selectedSaleIds.length} kayıt Grup ${pendingGroupNo} içine alınacak.` : "Önce bir grup seç veya Yeni Grup Ekle."}
                  </p>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setGroupModalOpen(false)} className="h-10 rounded-full border border-farm-border px-4 text-xs font-bold text-farm-muted">Vazgeç</button>
                    <button
                      type="button"
                      disabled={!pendingGroupNo || selectedSaleIds.length === 0 || saving}
                      onClick={() => pendingGroupNo && assignSelectedSalesToGroup(pendingGroupNo)}
                      className="h-10 rounded-full bg-farm-earth px-4 text-xs font-bold text-white hover:bg-farm-brown disabled:cursor-not-allowed disabled:opacity-45"
                    >
                      Gruba Kaydet
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {linkModalOpen && (
        <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/40 px-3">
          <div className="w-full max-w-6xl rounded-farm bg-white p-4 shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b border-farm-border pb-3">
              <div>
                <h3 className="font-heading text-lg font-bold text-farm-brown">Seçili Ön Satışları Hayvana Bağla</h3>
                <p className="font-body text-xs text-farm-muted">Sol tarafta seçilen kişiler, sağ tarafta devam eden veya hiç satışı olmayan hayvanlar. Bir ön satış birden fazla hayvana bölünmez.</p>
              </div>
              <button type="button" onClick={() => setLinkModalOpen(false)} className="h-8 w-8 rounded-full text-farm-muted hover:bg-farm-cream"><i className="ri-close-line" /></button>
            </div>

            <div className="mt-3 grid grid-cols-1 gap-2 rounded-farm border border-farm-border bg-farm-cream p-3 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-[10px] font-bold uppercase text-farm-muted">Küpe No Tam Eşleşme</span>
                <input value={linkKupeSearch} onChange={(e) => setLinkKupeSearch(e.target.value)} placeholder="Örn: 10" className="h-9 w-full rounded-farm border border-farm-border bg-white px-3 text-xs outline-none focus:border-farm-earth" />
              </label>
              <label className="block">
                <span className="mb-1 block text-[10px] font-bold uppercase text-farm-muted">Sipariş No Tam Eşleşme</span>
                <input value={linkSiparisSearch} onChange={(e) => setLinkSiparisSearch(e.target.value)} placeholder="Örn: 10" className="h-9 w-full rounded-farm border border-farm-border bg-white px-3 text-xs outline-none focus:border-farm-earth" />
              </label>
            </div>

            <div className="mt-4 grid max-h-[72vh] gap-4 overflow-hidden lg:grid-cols-[380px_minmax(0,1fr)]">
              <div className="min-h-0 rounded-farm border border-farm-border bg-farm-cream p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div>
                    <p className="font-heading text-sm font-bold text-farm-brown">Seçilen Ön Satışlar</p>
                    <p className="text-[11px] text-farm-muted">{selectedLinkSales.length} kayıt / {selectedNeededShareCount} hisse</p>
                  </div>
                  <button type="button" onClick={() => setSelectedSaleIds([])} className="rounded-full border border-farm-border bg-white px-2 py-1 text-[10px] font-bold text-farm-muted">Temizle</button>
                </div>
                <div className="max-h-[58vh] space-y-2 overflow-y-auto pr-1">
                  {selectedLinkSales.length === 0 ? (
                    <div className="rounded-farm border border-dashed border-farm-border bg-white px-3 py-8 text-center text-xs text-farm-muted">Bağlanmamış ön satış seç.</div>
                  ) : selectedLinkSales.map((sale) => (
                    <button key={sale.id} type="button" onClick={() => toggleSaleSelection(sale)} className="flex w-full items-start gap-2 rounded-farm border border-farm-border bg-white px-3 py-2 text-left hover:border-farm-earth">
                      <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded border border-emerald-600 bg-emerald-600 text-white"><i className="ri-check-line text-xs" /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-xs font-bold text-farm-brown">{sale.musteriAdi}</span>
                        <span className="block truncate text-[10px] text-farm-muted">{sale.paketBaslik} · {sale.adet} hisse · {formatTry(sale.toplamTutar)}</span>
                        <span className="block truncate font-mono text-[10px] text-farm-earth">{sale.satisNo}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="min-h-0 rounded-farm border border-farm-border bg-white p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div>
                    <p className="font-heading text-sm font-bold text-farm-brown">Hayvan Seç</p>
                    <p className="text-[11px] text-farm-muted">Yeterli boş hissesi olan hayvana bağla. Sistem boş hisseleri sırayla doldurur.</p>
                  </div>
                  <span className="rounded-full bg-farm-cream px-3 py-1 text-[11px] font-bold text-farm-brown">Gerekli: {selectedNeededShareCount}</span>
                </div>
                <div className="max-h-[58vh] space-y-2 overflow-y-auto pr-1">
                  {linkableAnimals.length === 0 ? (
                    <div className="rounded-farm border border-dashed border-farm-border bg-farm-cream px-3 py-8 text-center text-xs text-farm-muted">Boş hissesi olan hayvan yok.</div>
                  ) : linkableAnimals.map(({ animal, freeSlots }) => {
                    const enough = freeSlots.length >= selectedNeededShareCount && selectedNeededShareCount > 0;
                    const selected = animal.id === selectedLinkAnimalId;
                    const nextSlots = freeSlots.slice(0, selectedNeededShareCount);
                    return (
                      <button
                        key={animal.id}
                        type="button"
                        disabled={!enough}
                        onClick={() => setSelectedLinkAnimalId(animal.id)}
                        className={`grid w-full grid-cols-[1fr_auto] gap-3 rounded-farm border px-3 py-2 text-left transition ${selected ? "border-emerald-500 bg-emerald-50" : "border-farm-border bg-farm-cream hover:border-farm-earth"} disabled:cursor-not-allowed disabled:opacity-45`}
                      >
                        <span className="min-w-0">
                          <span className="block text-xs font-bold text-farm-brown">Küpe: {animal.kupeNo || "—"} · Sipariş: {animal.siraNo || "—"}</span>
                          <span className="block text-[10px] text-farm-muted">Boş hisse: {freeSlots.length} / {animal.toplamHisse} · Kullanılacak: {nextSlots.join(", ") || "—"}</span>
                        </span>
                        <span className={`rounded-full px-2 py-1 text-[10px] font-bold ${enough ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                          {enough ? "Uygun" : "Yetersiz"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-2 border-t border-farm-border pt-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-[11px] text-farm-muted">Bağlama işlemi API tarafında kilitli yapılır. Aynı hisse başka kullanıcı tarafından doldurulursa işlem uyarı verir.</p>
              <div className="flex gap-2">
                <button type="button" onClick={() => setLinkModalOpen(false)} className="h-10 rounded-full border border-farm-border px-4 text-xs font-bold text-farm-muted">Vazgeç</button>
                <button type="button" onClick={linkSelectedSalesToAnimal} disabled={saving || !selectedLinkAnimal || selectedLinkSales.length === 0} className="h-10 rounded-full bg-emerald-600 px-4 text-xs font-bold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-45">Seçili Ön Satışları Bağla</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {transferPickerOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-3">
          <div className="w-full max-w-4xl rounded-xl bg-white p-4 shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3">
              <div>
                <h4 className="text-sm font-bold text-gray-800">Aktif Havale Bağla</h4>
                <p className="text-[10px] text-gray-500">Boşta duran aktif havaleleri bu hisse ön satış kaporasına bağlar. Birden fazla kutucuğu seçebilirsin.</p>
              </div>
              <button type="button" onClick={() => setTransferPickerOpen(false)} className="h-8 w-8 rounded-full text-gray-400 hover:bg-gray-100"><i className="ri-close-line" /></button>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
              <input type="date" value={transferDateFrom} onChange={(event) => setTransferDateFrom(event.target.value)} className="h-10 rounded-lg border border-gray-200 px-3 text-xs outline-none focus:border-sky-400" title="Başlangıç tarihi" />
              <input type="date" value={transferDateTo} onChange={(event) => setTransferDateTo(event.target.value)} className="h-10 rounded-lg border border-gray-200 px-3 text-xs outline-none focus:border-sky-400" title="Bitiş tarihi" />
              <input value={transferDescriptionFilter} onChange={(event) => setTransferDescriptionFilter(event.target.value)} placeholder="Açıklama / Fiş No içinde ara" className="h-10 rounded-lg border border-gray-200 px-3 text-xs outline-none focus:border-sky-400" />
            </div>
            <div className="mt-3 max-h-[360px] divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-100">
              {activeBankTransfers.length === 0 ? (
                <div className="px-4 py-8 text-center text-xs text-gray-500">Aktif havale yok.</div>
              ) : activeBankTransfers.map((item) => {
                const checked = selectedTransferIds.includes(item.id);
                return (
                  <button key={item.id} type="button" onClick={() => toggleTransferSelection(item.id)} className={`grid w-full grid-cols-[28px_86px_86px_minmax(0,1fr)_100px] items-start gap-2 px-3 py-2.5 text-left text-xs hover:bg-sky-50 ${checked ? "bg-sky-50" : ""}`}>
                    <span className="pt-0.5"><span className={`flex h-4 w-4 items-center justify-center rounded border ${checked ? "border-sky-600 bg-sky-600 text-white" : "border-gray-300 bg-white"}`}>{checked && <i className="ri-check-line text-xs" />}</span></span>
                    <span className="font-semibold text-gray-500">{formatTransferDate(item.tarih)}</span>
                    <span className="font-bold text-sky-700">{item.fisNo}</span>
                    <span className="whitespace-normal break-words leading-snug text-gray-700">{item.aciklama || "—"}</span>
                    <span className="text-right font-bold text-gray-800">{Number(item.tutar || 0).toLocaleString("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 })}</span>
                  </button>
                );
              })}
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
              <p className="text-[10px] text-gray-500">{selectedTransferIds.length} havale seçildi. Seçili tutar kapora alanına aktarılır; kayıt sonrası havale pasife alınır.</p>
              <button type="button" onClick={addSelectedTransfersToSale} disabled={selectedTransferIds.length === 0} className="rounded-full bg-sky-600 px-4 py-2 text-xs font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-40">Seçili Havaleleri Bağla</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
